import { 
  STORAGE_KEYS, 
  ROUTES, 
  MENU_PAGE_MAPPING, 
  MENU_LABELS,
  FEATURES,
  FEATURE_TO_MENU_MAPPING,
  PAGE_TO_FEATURE_MAPPING
} from './constants.js';

/**
 * 서버 기반 권한 관리 클래스
 */
class RoleManager {
  constructor() {
    // 서버에서 받아온 피처 권한을 로컬 스토리지에서 로드
    this.userFeatures = this.loadUserFeatures();
  }

  /**
   * 로컬 스토리지에서 사용자의 피처 권한 로드
   */
  loadUserFeatures() {
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.USER_FEATURES);
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.error('피처 권한 로드 오류:', error);
      return [];
    }
  }

  /**
   * 피처 권한 새로고침 (로컬 스토리지에서 다시 로드)
   */
  refreshFeatures() {
    this.userFeatures = this.loadUserFeatures();
  }

  /**
   * 사용자의 피처 권한 가져오기
   */
  getUserFeatures() {
    return this.userFeatures;
  }

  /**
   * 피처 식별자를 메뉴 식별자로 변환
   * @param {string} featureName - 피처 식별자 (예: 'DASHBOARD')
   * @returns {string|null} 메뉴 식별자 (예: 'dashboard')
   */
  featureToMenuId(featureName) {
    return FEATURE_TO_MENU_MAPPING[featureName] || null;
  }

  /**
   * 피처 배열을 메뉴 식별자 배열로 변환
   * @param {string[]} features - 피처 식별자 배열
   * @returns {string[]} 메뉴 식별자 배열
   */
  featuresToMenuIds(features) {
    if (!features || !Array.isArray(features)) {
      return [];
    }

    return features
      .map(feature => this.featureToMenuId(feature))
      .filter(menuId => menuId !== null);
  }

  /**
   * 사용자가 접근 가능한 메뉴 식별자 목록 가져오기
   */
  getAccessibleMenuIds() {
    return this.featuresToMenuIds(this.userFeatures);
  }

  /**
   * 사용자가 특정 페이지에 접근할 수 있는지 확인
   * @param {string} page - 페이지 경로
   * @returns {boolean} 접근 가능 여부
   */
  hasPageAccess(page) {
    // 피처 권한이 없으면 접근 불가
    if (!this.userFeatures || this.userFeatures.length === 0) {
      return false;
    }

    // 1. 페이지 경로를 직접 피처로 매핑하는 경우 확인 (메뉴가 없는 상세 페이지 등)
    const directFeature = PAGE_TO_FEATURE_MAPPING[page];
    if (directFeature) {
      return this.userFeatures.includes(directFeature);
    }

    // 2. 페이지 경로를 메뉴 식별자로 변환
    const menuId = this.getMenuIdFromPage(page);
    if (!menuId) {
      // 매핑되지 않은 페이지의 경우 와일드카드 패턴 확인
      return this.checkWildcardAccess(page);
    }

    // 3. 사용자의 메뉴 접근 권한 확인
    const accessibleMenuIds = this.getAccessibleMenuIds();
    return accessibleMenuIds.includes(menuId);
  }

  /**
   * 와일드카드 패턴으로 페이지 접근 권한 확인
   * 예: /map-dashboard에 대한 권한이 있으면 /map-dashboard/* 에도 접근 가능
   */
  checkWildcardAccess(page) {
    // 페이지 경로를 분할하여 각 레벨별로 확인
    const pageParts = page.split('/').filter(part => part.length > 0);
    
    // 접근 가능한 메뉴 식별자 가져오기
    const accessibleMenuIds = this.getAccessibleMenuIds();
    
    for (const menuId of accessibleMenuIds) {
      // 메뉴 식별자에 해당하는 페이지 경로 가져오기
      const permissionRoute = this.getPageFromMenuId(menuId);
      if (permissionRoute) {
        const permissionParts = permissionRoute.split('/').filter(part => part.length > 0);
        
        // 권한 경로가 현재 페이지 경로의 접두사인지 확인
        if (this.isPathPrefix(permissionParts, pageParts)) {
          return true;
        }
      }
    }
    
    return false;
  }

  /**
   * 권한 경로가 페이지 경로의 접두사인지 확인
   */
  isPathPrefix(permissionParts, pageParts) {
    if (permissionParts.length > pageParts.length) {
      return false;
    }
    
    for (let i = 0; i < permissionParts.length; i++) {
      if (permissionParts[i] !== pageParts[i]) {
        return false;
      }
    }
    
    return true;
  }

  /**
   * 사용자가 특정 메뉴에 접근할 수 있는지 확인
   * @param {string} menu - 메뉴 표시명 또는 메뉴 식별자
   * @returns {boolean} 접근 가능 여부
   */
  hasMenuAccess(menu) {
    // 피처 권한이 없으면 접근 불가
    if (!this.userFeatures || this.userFeatures.length === 0) {
      return false;
    }

    // 메뉴 표시명을 메뉴 식별자로 변환 (표시명인 경우)
    let menuId = this.getMenuIdFromLabel(menu);
    
    // 이미 메뉴 식별자인 경우
    if (!menuId) {
      menuId = menu;
    }

    // 사용자의 메뉴 접근 권한 확인
    const accessibleMenuIds = this.getAccessibleMenuIds();
    return accessibleMenuIds.includes(menuId);
  }

  /**
   * 사용자가 특정 피처에 접근할 수 있는지 확인
   * @param {string} featureName - 피처 식별자 (예: 'DASHBOARD')
   * @returns {boolean} 접근 가능 여부
   */
  hasFeatureAccess(featureName) {
    return this.userFeatures.includes(featureName);
  }



  /**
   * 페이지 경로를 메뉴 식별자로 변환
   */
  getMenuIdFromPage(page) {
    for (const [menuId, route] of Object.entries(MENU_PAGE_MAPPING)) {
      if (route === page) {
        return menuId;
      }
    }
    return null;
  }

  /**
   * 메뉴 표시명을 메뉴 식별자로 변환
   */
  getMenuIdFromLabel(label) {
    for (const [menuId, menuLabel] of Object.entries(MENU_LABELS)) {
      if (menuLabel === label) {
        return menuId;
      }
    }
    return null;
  }

  /**
   * 메뉴 식별자를 페이지 경로로 변환
   */
  getPageFromMenuId(menuId) {
    return MENU_PAGE_MAPPING[menuId] || null;
  }

  /**
   * 메뉴 식별자를 표시명으로 변환
   */
  getLabelFromMenuId(menuId) {
    return MENU_LABELS[menuId] || menuId;
  }

  /**
   * 사용 가능한 메뉴 목록 가져오기 (표시명)
   */
  getAvailableMenus() {
    return Object.values(MENU_LABELS);
  }

  /**
   * 사용 가능한 페이지 목록 가져오기
   */
  getAvailablePages() {
    return Object.values(MENU_PAGE_MAPPING);
  }
}

// 싱글톤 인스턴스 생성
const roleManager = new RoleManager();

export default roleManager; 